import { CheckBox, ExpandLess, ExpandMore, ShoppingBasketOutlined, WarningAmberOutlined } from "@mui/icons-material";
import { Accordion, AccordionDetails, AccordionSummary, Alert, CircularProgress, Drawer, IconButton } from "@mui/material";
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { COMBOS_PATH, dailyMealsPath, DISCOUNT_STAFF_PATH, LOCAL_ORDERS_SNACKS_PATH, mealsPath, menuPath, PRICE_MEALS_EXTRA_PATH, SET_DAY_MENU_RANGE_PATH, snacksPath, studentsPath } from "../../../functions/src/admin/paths";
import { findSetDayMenuRangeForDate } from "../../../functions/src/common/setDayMenuRanges";
import { KEY_MAX_QUANTITY_LOCAL } from "../../../functions/src/api/constants";
import { toDateFromServer } from "../../../functions/src/api/dates";
import { calculateMealLinePrice, calculateSnackLinePrice, computeBasketCheckoutTotals, orderStats } from "../../../functions/src/common/orders";
import { evaluateBasketCheckoutValidity, type BasketCheckoutMenuContextByDate } from "../../../functions/src/common/basketCheckoutValidity";
import { formatCheckoutDeadlineAt, getStaleCheckoutDeadlineInstant } from "../../../functions/src/common/checkoutDeadlineUrgency";
import { roundEuro } from "../../../functions/src/common/pricing";
import type { Meal, MealLine, ModelComplexity, PriceMealsExtra, SnackLine } from "../../../functions/src/types";
import { auth, currentCatering, currentDailyMeals, currentMenu, DataSnapshot } from "../../api/firebase";
import { EMPTY_FILTER, useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { createOrderPaymentWithRetry, getCallableErrorMessage, initiateOrderCheckout, useBalance, useOrderCheckoutSession, useTopupDiscount, warmPaymentCallables } from "../../api/funds";
import { getLangValue, langContext, localeFromLang, type Language } from "../../api/Lang";
import LoadingSpinner from "../../api/LoadingSpinner";
import { getMealName, shouldShowPortionSize } from "../../api/mealUtils";
import { getPortionSizeLabel, usePortionSizeLabels } from "../../api/portionUtils";
import { formatPriceEuro } from "../../api/priceFormat";
import YesNoDlg from "../../api/YesNoDlg";
import { useStrings } from "../../common-strings";
import { MenuExtraChecklist } from "../Order";
import { useOrderDayDeadlineContext } from "../useOrderDayDeadlineContext";
import QuantitySelect from "../QuantitySelect";
import { OrderPerson } from "../../types";
import { useBasketStrings } from "./basket-strings";
import { emptyBasket, removeBasketDay, removeExpiredDaysFromBasket, removeSetMenuMealsFromBasketDay, updateBasketLineQuantity, checkoutSessionMatchesBasket, shouldRequoteStaleCheckoutSession } from "./basketActions";
import { useBasketContext } from "./BasketProvider";
import CheckoutDeadlineUrgencyAlert from "./CheckoutDeadlineUrgencyAlert";
import type { BasketEntry, StaleBasketEntry } from "./useBasket";
import type { StaleBasketEntryInput, StaleBasketGroup } from "../../../functions/src/common/basketStaleAttention";

type RecipientsMap = { [key: string]: { personnel?: boolean, student?: boolean } };

/** Global (not per-date) pricing data shared by every basket day. */
type BasketPricingShared = {
    sFoods: DataSnapshot;
    mealsVal: any;
    dailyMealsList: DataSnapshot<Meal>[];
    dailyMealsVal: any;
    snacksVal: any;
    recipients: RecipientsMap;
    /** Display name per student/staff id (first + last). */
    recipientNames: { [key: string]: string };
    staffDiscount: number;
    pricesExtra: PriceMealsExtra;
    combosData: any;
    discountPercentage: number;
    chargePackageExchangePrice: boolean;
    modelConstants: ModelComplexity | null | undefined;
    /** School snacks catalog (school DB); one listener for all day cards. */
    localSnacks: DataSnapshot | undefined;
    /** `localOrders/snacks` subtree; one listener instead of per-date. */
    localOrdersSnacksSnap: DataSnapshot | undefined;
    setDayMenuRangesRootSnap: DataSnapshot | undefined;
};

export default function BasketDrawer() {
    const [lang] = useContext(langContext);
    const strings = useBasketStrings();
    const commonStrings = useStrings();
    const { entries, staleEntries, staleGroups, totalBasketDays, entriesLoading, isOpen, closeBasket, basketError, setBasketError, runFulfillment, goToOrderDate, modelConstants, basketLocked, setBasketLocked, offerRemoveExpiredDays, checkoutUrgency, checkoutUrgencyDismissed, dismissCheckoutUrgency, openBasket, basketServerNow } = useBasketContext();
    const { uid } = auth.currentUser ?? {};

    // Basket entry list + badge use `useBasketEntries` in the provider; pricing RTDB only while checkout UI is open with items.
    const pricingListenersActive = isOpen && entries.length > 0;

    // Shared pricing data (same sources as Order.tsx / backend orderStats)
    const sFoods = useDatabase(pricingListenersActive ? mealsPath(currentCatering) : null);
    const sDailyMeals = useDatabase(
        pricingListenersActive ? dailyMealsPath(currentDailyMeals) : null,
        ref => ref.orderByChild('order').startAt(0)
    );
    const dailyMealsList: DataSnapshot<Meal>[] = useList(sDailyMeals) || [];
    const sSnacks = useDatabase(pricingListenersActive ? snacksPath(currentCatering) : null);
    const staffDiscount: number = useDatabase(pricingListenersActive ? DISCOUNT_STAFF_PATH : null)?.val() || 0;
    const [pricesExtra] = useDatabaseVal<PriceMealsExtra>(pricingListenersActive ? PRICE_MEALS_EXTRA_PATH : null);
    const [combosData] = useDatabaseVal(pricingListenersActive ? COMBOS_PATH : null);
    const orderPeople = useDatabase(pricingListenersActive && uid ? studentsPath(uid) : null);
    const localSnacks = useDatabase(pricingListenersActive ? snacksPath(currentCatering) : null, EMPTY_FILTER, false, false);
    const localOrdersSnacksSnap = useDatabase(pricingListenersActive ? LOCAL_ORDERS_SNACKS_PATH : null);
    const setDayMenuRangesRootSnap = useDatabase(pricingListenersActive ? SET_DAY_MENU_RANGE_PATH : null);
    const topupDiscount = useTopupDiscount();
    const discountPercentage = topupDiscount?.discountPercentage || 0;
    const balance = useBalance();
    const checkoutSession = useOrderCheckoutSession();
    const deadlineCtx = useOrderDayDeadlineContext();

    const [dlgEmptyBasket, setDlgEmptyBasket] = useState(false);
    const [emptying, setEmptying] = useState(false);
    const [menuContextByDate, setMenuContextByDate] = useState<BasketCheckoutMenuContextByDate>({});
    const [removingBlockedDays, setRemovingBlockedDays] = useState(false);
    const [removingStaleDays, setRemovingStaleDays] = useState(false);

    const recipients = useMemo(() => {
        const map: RecipientsMap = {};
        const names: { [key: string]: string } = {};
        orderPeople?.forEach(studentSnapshot => {
            const person = studentSnapshot.val() as OrderPerson;
            if (person) {
                map[studentSnapshot.key] = { personnel: person.personnel, student: person.student };
                const label = `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim();
                if (label) names[studentSnapshot.key] = label;
            }
        });
        return { map, names };
    }, [orderPeople]);

    const shared: BasketPricingShared = {
        sFoods,
        mealsVal: sFoods?.val(),
        dailyMealsList,
        dailyMealsVal: sDailyMeals?.val(),
        snacksVal: sSnacks?.val(),
        recipients: recipients.map,
        recipientNames: recipients.names,
        staffDiscount,
        pricesExtra,
        combosData,
        discountPercentage,
        chargePackageExchangePrice: !!modelConstants?.chargePackageExchangePrice,
        modelConstants,
        localSnacks,
        localOrdersSnacksSnap,
        setDayMenuRangesRootSnap,
    };

    // Per-day prices reported by the day cards (undefined = still loading)
    const [dayPrices, setDayPrices] = useState<{ [date: string]: number | undefined }>({});
    const onPriced = useCallback((date: string, price: number | undefined) => {
        setDayPrices(prev => (prev[date] === price ? prev : { ...prev, [date]: price }));
    }, []);

    const entryDatesKey = entries.map(e => e.date).join(',');
    useEffect(() => {
        if (!pricingListenersActive) {
            setDayPrices({});
            return;
        }
        setDayPrices(prev => {
            const next: { [date: string]: number | undefined } = {};
            for (const date of entryDatesKey ? entryDatesKey.split(',') : []) {
                if (date && prev[date] !== undefined) next[date] = prev[date];
            }
            return next;
        });
    }, [entryDatesKey, pricingListenersActive]);

    useEffect(() => {
        if (!pricingListenersActive) {
            setMenuContextByDate({});
            return;
        }
        setMenuContextByDate(prev => {
            const next: BasketCheckoutMenuContextByDate = {};
            for (const date of entryDatesKey ? entryDatesKey.split(',') : []) {
                if (date && prev[date]) {
                    next[date] = prev[date];
                }
            }
            return next;
        });
    }, [entryDatesKey, pricingListenersActive]);

    const onMenuCheckoutContext = useCallback((date: string, ctx: BasketCheckoutMenuContextByDate[string]) => {
        setMenuContextByDate(prev => {
            const prevCtx = prev[date];
            if (prevCtx?.ready === ctx.ready && prevCtx?.menuMealsForOrderDate === ctx.menuMealsForOrderDate) {
                return prev;
            }
            return { ...prev, [date]: ctx };
        });
    }, []);

    const checkoutValidity = useMemo(() => {
        if (!pricingListenersActive || !deadlineCtx || !basketServerNow || entries.length === 0) {
            return { ready: false, blocked: [] as { date: string; message: string }[] };
        }
        return evaluateBasketCheckoutValidity(
            entries.map(e => ({ date: e.date, draft: e.draft, placed: e.placed })),
            menuContextByDate,
            deadlineCtx,
            lang,
            basketServerNow,
        );
    }, [pricingListenersActive, deadlineCtx, basketServerNow, entries, menuContextByDate, lang]);

    const checkoutAllowed = checkoutValidity.ready && checkoutValidity.blocked.length === 0 && staleEntries.length === 0;
    const checkoutBlockedByDeadlines =
        staleEntries.length > 0 ||
        (checkoutValidity.ready && checkoutValidity.blocked.length > 0);

    const pricesReady = entries.every(e => dayPrices[e.date] !== undefined);
    const checkoutTotals = useMemo(() => {
        if (!pricesReady) return null;
        return computeBasketCheckoutTotals(
            entries.map(e => ({ newPrice: dayPrices[e.date] ?? 0, oldPrice: e.oldPrice })),
            balance ?? 0
        );
    }, [entries, dayPrices, balance, pricesReady]);
    const { netDue = 0, creditsRefund = 0, creditsUsed = 0, payAmount = 0, balanceAfterCheckout = 0 } = checkoutTotals ?? {};
    const hasWalletCredits = roundEuro(balance ?? 0) > 0;
    const showCreditsDeduction = netDue > 0 && hasWalletCredits;

    const [paying, setPaying] = useState(false);

    useEffect(() => {
        if (basketLocked) {
            setDlgEmptyBasket(false);
        }
    }, [basketLocked]);

    // Boot the checkout → Viva callables while the parent reviews the basket, so Pay does not wait on cold starts.
    useEffect(() => {
        if (isOpen && entries.length > 0 && !basketLocked) {
            warmPaymentCallables('orderCheckout');
        }
    }, [isOpen, entries.length, basketLocked]);

    const onPay = useCallback(async () => {
        if (basketLocked || !entries.length || !pricesReady) return;
        setPaying(true);
        setBasketLocked(true);
        setBasketError(null);
        let keepLockedAfterPay = false;
        try {
            const dates = entries.map(e => e.date);
            const canReuseQuote = checkoutSession
                && checkoutSessionMatchesBasket(checkoutSession, dates, payAmount)
                && !shouldRequoteStaleCheckoutSession(checkoutSession, balance ?? 0);

            let quotePayAmount = payAmount;
            if (!canReuseQuote) {
                const quote = await initiateOrderCheckout(dates, lang);
                quotePayAmount = quote.payAmount;
            }
            if (quotePayAmount > 0) {
                // Redirects to Viva — the #paySuccess / #payFail handlers take over from here
                keepLockedAfterPay = true;
                await createOrderPaymentWithRetry(quotePayAmount, 'orderCheckout');
            } else {
                // €0 basket (e.g. package meal exchanges) or fully covered by credits
                closeBasket();
                await runFulfillment(false);
            }
        } catch (error) {
            console.error('Basket checkout failed:', error);
            if (offerRemoveExpiredDays(error, onPay)) {
                keepLockedAfterPay = false;
                return;
            }
            setBasketError(getCallableErrorMessage(error, strings.checkoutFailedTryAgain));
            keepLockedAfterPay = false;
        } finally {
            setPaying(false);
            if (!keepLockedAfterPay) {
                setBasketLocked(false);
            }
        }
    }, [basketLocked, entries, pricesReady, setBasketLocked, setBasketError, checkoutSession, payAmount, balance, lang, closeBasket, runFulfillment, offerRemoveExpiredDays, strings.checkoutFailedTryAgain]);

    const onRemoveBlockedDays = async () => {
        if (basketLocked || removingBlockedDays || checkoutValidity.blocked.length === 0) return;
        setRemovingBlockedDays(true);
        setBasketError(null);
        try {
            await removeExpiredDaysFromBasket(
                checkoutValidity.blocked.map(b => b.date),
                entries.map(e => ({ date: e.date, kind: e.kind })),
            );
        } catch (error) {
            console.error('removeBlockedDays failed:', error);
            setBasketError(getCallableErrorMessage(error, strings.checkoutFailedTryAgain));
        } finally {
            setRemovingBlockedDays(false);
        }
    };

    const onRemoveAllStaleDays = async () => {
        if (basketLocked || removingStaleDays || staleEntries.length === 0 || !deadlineCtx) return;
        setRemovingStaleDays(true);
        setBasketError(null);
        try {
            await Promise.all(staleEntries.map(async (entry) => {
                if (entry.fixKind === 'remove_set_menu_meals') {
                    await removeSetMenuMealsFromBasketDay(entry.date, entry.draft, deadlineCtx);
                } else {
                    await removeBasketDay(entry.date, entry.kind);
                }
            }));
        } catch (error) {
            console.error('removeStaleDays failed:', error);
            setBasketError(getCallableErrorMessage(error, strings.checkoutFailedTryAgain));
        } finally {
            setRemovingStaleDays(false);
        }
    };

    const payBlockedHintRemoving = removingStaleDays || removingBlockedDays;
    const onPayBlockedHintAction = () => {
        if (basketLocked || payBlockedHintRemoving) return;
        if (staleEntries.length > 0) {
            void onRemoveAllStaleDays();
            return;
        }
        if (checkoutValidity.ready && checkoutValidity.blocked.length > 0) {
            void onRemoveBlockedDays();
        }
    };

    const onConfirmEmptyBasket = async () => {
        if (basketLocked) return;
        setDlgEmptyBasket(false);
        setEmptying(true);
        setBasketLocked(true);
        setBasketError(null);
        try {
            await emptyBasket([
                ...entries.map(e => ({ date: e.date, kind: e.kind })),
                ...staleEntries.map(e => ({ date: e.date, kind: e.kind })),
            ]);
        } catch (error) {
            console.error('emptyBasket failed:', error);
            setBasketError(strings.checkoutFailedTryAgain);
        } finally {
            setEmptying(false);
            setBasketLocked(false);
        }
    };

    return (
        <Drawer
            anchor="right"
            open={isOpen}
            onClose={(_event, reason) => {
                if (basketLocked) return;
                closeBasket();
            }}
            slotProps={{ paper: { className: 'basket-drawer-paper', 'aria-labelledby': 'basket-drawer-title' } }}
            sx={{ '& .MuiDrawer-paper': { width: { xs: '100%', sm: 500 }, maxWidth: '100vw' } }}
        >
            <div className="basket-drawer">
                {dlgEmptyBasket &&
                    <YesNoDlg
                        title={strings.emptyBasketTitle}
                        msg={strings.emptyBasketMsg}
                        yes={commonStrings.yes}
                        no={commonStrings.no}
                        onNo={() => setDlgEmptyBasket(false)}
                        onYes={onConfirmEmptyBasket}
                    />
                }

                <IconButton className="basket-drawer-close" onClick={closeBasket} disabled={basketLocked} aria-label={strings.close}>
                    <img src="/img-assets/x-circled3.svg" width={32} height={32} alt="" />
                </IconButton>

                <div className="basket-drawer-header">
                    <ShoppingBasketOutlined className="basket-drawer-title-icon" aria-hidden />
                    <h2 id="basket-drawer-title" className="basket-drawer-title">{strings.title}</h2>
                    {totalBasketDays > 0 && !entriesLoading && !basketLocked && (
                        <button type="button" className="basket-empty-link" onClick={() => setDlgEmptyBasket(true)}>
                            {strings.emptyBasket}
                        </button>
                    )}
                </div>

                {checkoutUrgency.active && !checkoutUrgencyDismissed && (
                    <CheckoutDeadlineUrgencyAlert
                        urgency={checkoutUrgency}
                        variant="drawer"
                        modelConstants={modelConstants}
                        onOpenBasket={openBasket}
                    />
                )}

                {(entriesLoading || emptying) && <LoadingSpinner />}

                {!entriesLoading && !emptying && totalBasketDays === 0 && (
                    <div className="basket-empty">
                        <div className="basket-empty-title">{strings.empty}</div>
                        <div className="basket-empty-hint">{strings.emptyHint}</div>
                    </div>
                )}

                {!entriesLoading && !emptying && staleEntries.length > 0 && entries.length === 0 && (
                    <p className="basket-stale-only-hint">{strings.staleOnlyValidHint}</p>
                )}

                {!entriesLoading && !emptying && staleEntries.length > 0 &&
                    <StaleBasketAttentionAccordion
                        staleEntries={staleEntries}
                        staleGroups={staleGroups}
                        strings={strings}
                        lang={lang}
                        modelConstants={modelConstants}
                        removing={removingStaleDays}
                        disabled={basketLocked}
                        onRemoveDay={(entry) => removeBasketDay(entry.date, entry.kind)}
                        onRemoveSetMenuMeals={async (entry) => {
                            if (!deadlineCtx) return;
                            await removeSetMenuMealsFromBasketDay(entry.date, entry.draft, deadlineCtx);
                        }}
                        onEditDay={(entry) => goToOrderDate(entry.date)}
                        onRemoveAll={() => void onRemoveAllStaleDays()}
                    />
                }

                {pricingListenersActive && (
                    <>
                        <div className={`basket-drawer-body${basketLocked ? ' basket-drawer-body--locked' : ''}`} aria-busy={basketLocked}>
                            <div className="basket-days">
                                {entries.map(entry => {
                                    const blockedDay = checkoutValidity.blocked.find(b => b.date === entry.date);
                                    return (
                                    <BasketDayCard
                                        key={entry.date}
                                        entry={entry}
                                        shared={shared}
                                        onPriced={onPriced}
                                        onGoToDay={goToOrderDate}
                                        onMenuCheckoutContext={onMenuCheckoutContext}
                                        checkoutBlockedMessage={blockedDay?.message ?? null}
                                    />
                                    );
                                })}
                            </div>

                            <div className="basket-summary">
                                {showCreditsDeduction && (
                                    <div className="basket-summary-row basket-summary-total">
                                        <span>{strings.total}</span>
                                        <span>{pricesReady ? formatPriceEuro(netDue, lang) : '---'}</span>
                                    </div>
                                )}

                                {netDue < 0 && (
                                    <div className="basket-credits basket-credits-refund">
                                        <div className="basket-summary-row">
                                            <span className="basket-credits-refund-label">{strings.creditsRefundLabel}</span>
                                            <span className="basket-credits-amount">+{formatPriceEuro(creditsRefund, lang)}</span>
                                        </div>
                                        <div className="basket-credits-remain">
                                            {strings.willBecomePrefix}{' '}
                                            <span>{pricesReady ? formatPriceEuro(balanceAfterCheckout, lang) : '---'}</span>
                                        </div>
                                    </div>
                                )}

                                {showCreditsDeduction && (
                                    <div className="basket-credits" role="group" aria-labelledby="basket-credits-deduction-label">
                                        <div className="basket-summary-row basket-credits-row">
                                            <div className="basket-credits-left">
                                                <span className="basket-credits-applied" aria-hidden="true">
                                                    <CheckBox className="basket-credits-applied-icon" />
                                                </span>
                                                <span id="basket-credits-deduction-label" className="basket-credits-label">{strings.creditsLabel}</span>
                                            </div>
                                            <span className="basket-credits-amount">
                                                {pricesReady && creditsUsed > 0 ? `-${formatPriceEuro(creditsUsed, lang)}` : ''}
                                            </span>
                                        </div>
                                        <div className="basket-credits-remain">
                                            {strings.willRemainPrefix}{' '}
                                            <span>{pricesReady ? formatPriceEuro(balanceAfterCheckout, lang) : '---'}</span>
                                        </div>
                                    </div>
                                )}

                                {basketError && (
                                    <Alert severity="warning" className="basket-error-alert" onClose={() => setBasketError(null)}>
                                        {basketError}
                                    </Alert>
                                )}

                                <div className="basket-summary-row basket-summary-new-total">
                                    <span>{showCreditsDeduction ? strings.newTotal : strings.total}</span>
                                    <span>{pricesReady ? formatPriceEuro(payAmount, lang) : '---'}</span>
                                </div>
                            </div>
                        </div>
                    </>
                )}

                {!entriesLoading && !emptying && totalBasketDays > 0 &&
                    <div className="basket-drawer-footer">
                        {checkoutBlockedByDeadlines && (
                            <button
                                type="button"
                                id="basket-pay-blocked-hint"
                                className="basket-pay-blocked-hint"
                                disabled={basketLocked || payBlockedHintRemoving}
                                onClick={onPayBlockedHintAction}
                            >
                                {strings.payBlockedHint}
                            </button>
                        )}
                        {checkoutValidity.ready && checkoutValidity.blocked.length > 0 && !basketLocked && entries.length > 0 && (
                            <button
                                type="button"
                                className="basket-empty-link basket-remove-blocked-link"
                                disabled={removingBlockedDays}
                                onClick={() => void onRemoveBlockedDays()}
                            >
                                {strings.removeBlockedDays}
                            </button>
                        )}
                        {entries.length > 0 &&
                            <button
                                className={`order-completed-button2 basket-pay-button ${(!pricesReady || !checkoutAllowed || paying) && 'disabled' || ''}`}
                                disabled={!pricesReady || !checkoutAllowed || paying || basketLocked}
                                aria-busy={paying}
                                aria-describedby={checkoutBlockedByDeadlines ? 'basket-pay-blocked-hint' : undefined}
                                onClick={onPay}
                            >
                                {paying ? (
                                    <span className="basket-pay-button-content">
                                        <CircularProgress size={18} color="inherit" aria-hidden />
                                        {payAmount > 0 ? strings.paying : strings.registering}
                                    </span>
                                ) : (payAmount > 0 ? strings.payButton : strings.completeButton)}
                            </button>
                        }
                    </div>
                }
            </div>
        </Drawer>
    );
}

function staleRowDetailMessage(
    item: StaleBasketEntryInput,
    strings: ReturnType<typeof useBasketStrings>,
    lang: Language,
    modelConstants: ModelComplexity | null,
): string {
    if (item.category === 'no_orderable_meals') {
        return item.message;
    }
    const instant = getStaleCheckoutDeadlineInstant(item.date, item.category, modelConstants);
    if (!instant) {
        return item.message;
    }
    const at = formatCheckoutDeadlineAt(instant, lang);
    if (item.category === 'set_menu_deadline') {
        return strings.staleSetMenuDeadlineAt(at);
    }
    return strings.staleGeneralDeadlineAt(at);
}

type StaleBasketAttentionAccordionProps = {
    staleEntries: StaleBasketEntry[];
    staleGroups: StaleBasketGroup[];
    strings: ReturnType<typeof useBasketStrings>;
    lang: Language;
    modelConstants: ModelComplexity | null;
    removing: boolean;
    disabled: boolean;
    onRemoveDay: (entry: StaleBasketEntry) => Promise<void>;
    onRemoveSetMenuMeals: (entry: StaleBasketEntry) => Promise<void>;
    onEditDay: (entry: StaleBasketEntry) => void;
    onRemoveAll: () => void;
};

function StaleBasketAttentionAccordion({ staleEntries, staleGroups, strings, lang, modelConstants, removing, disabled, onRemoveDay, onRemoveSetMenuMeals, onEditDay, onRemoveAll }: StaleBasketAttentionAccordionProps) {
    const staleByDate = useMemo(() => new Map(staleEntries.map(e => [e.date, e])), [staleEntries]);
    const [removingDate, setRemovingDate] = useState<string | null>(null);
    const hasPartialFix = staleEntries.some(e => e.fixKind === 'remove_set_menu_meals');
    const hasFullRemove = staleEntries.some(e => e.fixKind === 'remove_day');
    const attentionHint = hasPartialFix && hasFullRemove
        ? strings.staleAttentionHintMixed
        : hasPartialFix
            ? strings.staleAttentionHintPartial
            : strings.staleAttentionHint;

    const formatDateLabel = (date: string): string =>
        toDateFromServer(date).toLocaleDateString(localeFromLang(lang), { day: 'numeric', month: 'long', year: 'numeric' });

    const handleRemoveDay = async (entry: StaleBasketEntry) => {
        if (removing || removingDate || disabled) return;
        setRemovingDate(entry.date);
        try {
            await onRemoveDay(entry);
        } finally {
            setRemovingDate(null);
        }
    };

    const handleRemoveSetMenuMeals = async (entry: StaleBasketEntry) => {
        if (removing || removingDate || disabled) return;
        setRemovingDate(entry.date);
        try {
            await onRemoveSetMenuMeals(entry);
        } finally {
            setRemovingDate(null);
        }
    };

    return (
        <Accordion className="basket-stale-accordion" disableGutters elevation={0}>
            <AccordionSummary expandIcon={<ExpandMore />} className="basket-stale-accordion-summary">
                <WarningAmberOutlined className="basket-stale-accordion-icon" color="warning" aria-hidden />
                <span className="basket-stale-accordion-title">
                    {strings.staleAttentionTitle} ({staleEntries.length})
                </span>
            </AccordionSummary>
            <AccordionDetails className="basket-stale-accordion-details">
                <p className="basket-stale-accordion-hint">{attentionHint}</p>
                {staleGroups.map(group => (
                    <div key={group.category} className="basket-stale-group">
                        {group.category === 'no_orderable_meals' && (
                            <div className="basket-stale-group-title">{strings.staleGroupNoOrderableMeals}</div>
                        )}
                        <ul className="basket-stale-group-list">
                            {group.items.map(item => {
                                const entry = staleByDate.get(item.date);
                                if (!entry) return null;
                                return (
                                    <li key={item.date} className="basket-stale-row">
                                        <div className="basket-stale-row-main">
                                            <span className="basket-stale-row-date">{strings.staleOrderForDay(formatDateLabel(item.date))}</span>
                                            <span className="basket-stale-row-msg">
                                                {staleRowDetailMessage(item, strings, lang, modelConstants)}
                                            </span>
                                        </div>
                                        {entry.fixKind === 'remove_set_menu_meals' ? (
                                            <div className="basket-stale-row-actions">
                                                <button
                                                    type="button"
                                                    className="basket-empty-link basket-stale-row-remove"
                                                    disabled={disabled || removing || removingDate === item.date}
                                                    onClick={() => void handleRemoveSetMenuMeals(entry)}
                                                >
                                                    {strings.staleRemoveSetMenuMeals}
                                                </button>
                                                <button
                                                    type="button"
                                                    className="basket-empty-link basket-stale-row-edit"
                                                    disabled={disabled || removing}
                                                    onClick={() => onEditDay(entry)}
                                                >
                                                    {strings.staleEditDay}
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                type="button"
                                                className="basket-empty-link basket-stale-row-remove"
                                                disabled={disabled || removing || removingDate === item.date}
                                                onClick={() => void handleRemoveDay(entry)}
                                            >
                                                {strings.removeDay}
                                            </button>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                ))}
                <button
                    type="button"
                    className="basket-empty-link basket-stale-remove-all"
                    disabled={disabled || removing}
                    onClick={onRemoveAll}
                >
                    {strings.staleRemoveAll}
                </button>
            </AccordionDetails>
        </Accordion>
    );
}

type BasketDayCardProps = {
    entry: BasketEntry;
    shared: BasketPricingShared;
    onPriced: (date: string, price: number | undefined) => void;
    onGoToDay: (date: string) => void;
    onMenuCheckoutContext: (date: string, ctx: BasketCheckoutMenuContextByDate[string]) => void;
    checkoutBlockedMessage: string | null;
};

function BasketDayCard({ entry, shared, onPriced, onGoToDay, onMenuCheckoutContext, checkoutBlockedMessage }: BasketDayCardProps) {
    const [lang] = useContext(langContext);
    const strings = useBasketStrings();
    const { uid } = auth.currentUser ?? {};
    const { date, draft, kind, oldPrice, placed } = entry;
    const [expanded, setExpanded] = useState(true);
    const [removing, setRemoving] = useState(false);

    // Per-date menu data (pricing for menu items + side/dessert)
    const menusSnapshot = useDatabase(`${menuPath(currentMenu)}/${date}`);
    const sideDish: Meal = menusSnapshot?.child('sideMenu')?.val();
    const dessert: Meal = menusSnapshot?.child('dessertMenu')?.val();
    const menuItems = useList(menusSnapshot);
    const menuData: { [key: string]: Meal } = useMemo(() => {
        const data: { [key: string]: Meal } = {};
        menuItems?.forEach(menuItemSnapshot => {
            const menuItem = menuItemSnapshot.val();
            if (menuItem && menuItem.key) {
                data[menuItem.key] = menuItem;
            }
        });
        return data;
    }, [menuItems]);

    const setDayMenuRangesVal = useMemo(
        () => shared.setDayMenuRangesRootSnap?.val() as Record<string, unknown> | null | undefined,
        [shared.setDayMenuRangesRootSnap]
    );
    const setDayPrice = findSetDayMenuRangeForDate(setDayMenuRangesVal, date)?.price ?? null;
    const totalSetDayPrice = useMemo(() => {
        if (!setDayPrice) return null;
        const uniqueRecipients = new Set<string>();
        [...Object.values(draft.meals ?? {}), ...Object.values(draft.snacks ?? {})].map(line => {
            if (line?.recipient) uniqueRecipients.add(line.recipient);
            return null;
        });
        return setDayPrice * uniqueRecipients.size;
    }, [setDayPrice, draft]);

    const { mealsVal, dailyMealsVal, snacksVal, recipients, staffDiscount, pricesExtra, combosData, discountPercentage, chargePackageExchangePrice, sFoods, dailyMealsList, modelConstants } = shared;
    const portionLabels = usePortionSizeLabels();

    const { totalPrice = 0 } = useMemo(() =>
        orderStats(uid, draft, snacksVal, mealsVal, dailyMealsVal, recipients, staffDiscount, pricesExtra, sideDish, dessert, totalSetDayPrice, combosData, discountPercentage, menuData, chargePackageExchangePrice) ?? { totalPrice: 0 },
        [uid, draft, snacksVal, mealsVal, dailyMealsVal, recipients, staffDiscount, pricesExtra, sideDish, dessert, totalSetDayPrice, combosData, discountPercentage, menuData, chargePackageExchangePrice]
    );

    // Report the day price up once the per-date menu data has resolved
    const priceReady = !!menusSnapshot;
    useEffect(() => {
        onPriced(date, priceReady ? roundEuro(totalPrice) : undefined);
    }, [date, totalPrice, priceReady, onPriced]);

    useEffect(() => {
        if (!priceReady) {
            onMenuCheckoutContext(date, { ready: false });
            return;
        }
        onMenuCheckoutContext(date, { ready: true, menuMealsForOrderDate: menuData });
    }, [date, priceReady, menuData, onMenuCheckoutContext]);

    const dateObj = toDateFromServer(date);
    const dateLabel = dateObj.toLocaleDateString(localeFromLang(lang), { day: 'numeric', month: 'long', year: 'numeric' });

    const snacksMaxLocal = shared.localOrdersSnacksSnap?.child(date);

    const mealLines = Object.entries(draft.meals ?? {}).filter(([, line]) => line?.key && line.quantity > 0);
    const snackLines = Object.entries(draft.snacks ?? {}).filter(([, line]) => line?.snackKey && line.quantity > 0);

    const { recipientNames } = shared;

    const mealLinePrice = (line: MealLine): number =>
        calculateMealLinePrice(line, mealsVal, dailyMealsVal, recipients, staffDiscount, pricesExtra, sideDish, dessert, combosData, discountPercentage, menuData, chargePackageExchangePrice) ?? 0;
    const snackLinePrice = (line: SnackLine): number =>
        calculateSnackLinePrice(line, snacksVal, recipients, staffDiscount, discountPercentage) ?? 0;

    const runRemove = async (action: () => Promise<void>) => {
        if (removing) return;
        setRemoving(true);
        try {
            await action();
        } catch (error) {
            console.error('Basket remove failed:', error);
        } finally {
            setRemoving(false);
        }
    };

    const onQuantityChange = (lineType: 'meal' | 'snack', lineId: string, mealLine?: MealLine) => (quantity: number) => {
        void runRemove(() => updateBasketLineQuantity({ date, kind, lineType, lineId, quantity, placed, mealLine }));
    };

    const onEditDay = () => onGoToDay(date);

    const getMealFoodItem = (line: MealLine): Meal | undefined =>
        menuData[line.key] || mealsVal?.[line.key] || dailyMealsList.find(mealSnapshot => mealSnapshot.key === line.key)?.val();

    const formatMealItemLabel = (line: MealLine, foodItem: Meal | undefined): string => {
        const mealLabel = getMealName(line.key, sFoods, dailyMealsList, lang, menuData) || line.key;
        if (!line.portionSize || !foodItem || !modelConstants?.allowChangePortions || !shouldShowPortionSize(foodItem, combosData)) {
            return mealLabel;
        }
        return `${mealLabel} (${getPortionSizeLabel(line.portionSize, portionLabels)})`;
    };

    const getSnackMaxQuantity = (line: SnackLine): number => {
        const s_localSnack = shared.localSnacks?.child(line.snackKey);
        const maxQuantityLocal = s_localSnack?.child(KEY_MAX_QUANTITY_LOCAL)?.val() || 1000000;
        const snacksRemaining = maxQuantityLocal - (snacksMaxLocal?.child(line.snackKey)?.val() ?? 0);
        return Math.min(9, snacksRemaining + line.quantity);
    };

    return (
        <div className={`basket-day-card${checkoutBlockedMessage ? ' basket-day-card--blocked' : ''}${removing ? ' basket-day-card-removing' : ''}`}>
            {checkoutBlockedMessage &&
                <Alert severity="warning" className="basket-day-blocked-alert">
                    {checkoutBlockedMessage}
                </Alert>
            }
            <div className={`basket-day-header${expanded ? ' basket-day-header--expanded' : ''}`}>
                <button
                    type="button"
                    className="basket-day-toggle"
                    onClick={() => setExpanded(!expanded)}
                    aria-expanded={expanded}
                >
                    <span className="basket-day-toggle-chevron" aria-hidden="true">
                        {expanded ? <ExpandLess /> : <ExpandMore />}
                    </span>
                    <span className="basket-day-date">{dateLabel}</span>
                    {kind === 'change' && <span className="basket-day-chip">{strings.changeLabel}</span>}
                </button>
                <span className="basket-day-price">{priceReady ? formatPriceEuro(roundEuro(totalPrice), lang) : '---'}</span>
            </div>

            {expanded && (
                <div className="basket-day-lines">
                    {buildRecipientGroups({
                        mealLines,
                        snackLines,
                        recipientNames,
                        getMealFoodItem,
                        formatMealItemLabel,
                        sideDish,
                        dessert,
                        date,
                        lang,
                        snacksVal,
                        getSnackMaxQuantity,
                        mealLinePrice,
                        snackLinePrice,
                        onQuantityChange,
                        strings,
                        removing,
                    })}
                    {kind === 'change' && oldPrice > 0 && (
                        <div className="basket-line basket-line-refund">
                            <div className="basket-line-row">
                                <span>{strings.previousOrderLabel}</span>
                                <div className="basket-line-right">
                                    <span className="basket-line-qty-spacer" aria-hidden="true" />
                                    <span className="basket-line-price">-{formatPriceEuro(oldPrice, lang)}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <button type="button" className="basket-edit-day-link" onClick={onEditDay}>
                {strings.editDay}
            </button>
            {checkoutBlockedMessage &&
                <button
                    type="button"
                    className="basket-empty-link basket-day-remove-blocked"
                    disabled={removing}
                    onClick={() => void runRemove(() => removeBasketDay(date, kind))}
                >
                    {strings.removeDay}
                </button>
            }
        </div>
    );
}

type BasketLineItem = {
    key: string;
    itemLabel: string;
    extras?: React.ReactNode;
    quantity: number;
    maxQuantity: number;
    price: string;
    onQuantityChange: (quantity: number) => void;
    quantityLabel: string;
    disabled?: boolean;
};

type BasketRecipientGroup = {
    recipientKey: string;
    personLabel: string;
    lines: BasketLineItem[];
};

type BuildRecipientGroupsArgs = {
    mealLines: [string, MealLine][];
    snackLines: [string, SnackLine][];
    recipientNames: { [key: string]: string };
    getMealFoodItem: (line: MealLine) => Meal | undefined;
    formatMealItemLabel: (line: MealLine, foodItem: Meal | undefined) => string;
    sideDish: Meal;
    dessert: Meal;
    date: string;
    lang: Language;
    snacksVal: BasketPricingShared['snacksVal'];
    getSnackMaxQuantity: (line: SnackLine) => number;
    mealLinePrice: (line: MealLine) => number;
    snackLinePrice: (line: SnackLine) => number;
    onQuantityChange: (lineType: 'meal' | 'snack', lineId: string, mealLine?: MealLine) => (quantity: number) => void;
    strings: ReturnType<typeof useBasketStrings>;
    removing: boolean;
};

function buildRecipientGroups(args: BuildRecipientGroupsArgs): React.ReactNode {
    const groups: BasketRecipientGroup[] = [];
    const groupIndexByRecipient = new Map<string, number>();

    const addLine = (recipientKey: string, personLabel: string, line: BasketLineItem) => {
        const mapKey = recipientKey || '__none__';
        const existingIndex = groupIndexByRecipient.get(mapKey);
        if (existingIndex === undefined) {
            groupIndexByRecipient.set(mapKey, groups.length);
            groups.push({ recipientKey, personLabel, lines: [line] });
            return;
        }
        groups[existingIndex].lines.push(line);
    };

    args.mealLines.forEach(([lineId, line]) => {
        const foodItem = args.getMealFoodItem(line);
        const recipientKey = line.recipient ?? '';
        const personLabel = line.recipient ? args.recipientNames[line.recipient] : '';
        addLine(recipientKey, personLabel, {
            key: `meal-${lineId}`,
            itemLabel: args.formatMealItemLabel(line, foodItem),
            extras: foodItem ? (
                <MenuExtraChecklist
                    meal={foodItem}
                    date={args.date}
                    quantitySide={line.quantitySide ?? 0}
                    quantityDessert={line.quantityDessert ?? 0}
                    sideDish={args.sideDish}
                    dessert={args.dessert}
                    variant="inlineBasket"
                />
            ) : null,
            quantity: line.quantity,
            maxQuantity: line.isPackage ? 1 : 9,
            price: formatPriceEuro(roundEuro(args.mealLinePrice(line)), args.lang),
            onQuantityChange: args.onQuantityChange('meal', lineId, line),
            quantityLabel: args.strings.quantityLabel,
            disabled: args.removing,
        });
    });

    args.snackLines.forEach(([lineId, line]) => {
        const recipientKey = line.recipient ?? '';
        const personLabel = line.recipient ? args.recipientNames[line.recipient] : '';
        addLine(recipientKey, personLabel, {
            key: `snack-${lineId}`,
            itemLabel: getLangValue(args.snacksVal?.[line.snackKey]?.name, args.lang) || line.snackKey,
            quantity: line.quantity,
            maxQuantity: args.getSnackMaxQuantity(line),
            price: formatPriceEuro(roundEuro(args.snackLinePrice(line)), args.lang),
            onQuantityChange: args.onQuantityChange('snack', lineId),
            quantityLabel: args.strings.quantityLabel,
            disabled: args.removing,
        });
    });

    return groups.map((group, groupIndex) => (
        <div
            key={group.recipientKey || `group-${groupIndex}`}
            className={`basket-student-group ${groupIndex > 0 ? 'basket-student-group--spaced' : ''}`}
        >
            {group.personLabel && <div className="basket-student-name">{group.personLabel}</div>}
            {group.lines.map(line => (
                <BasketLineRow
                    key={line.key}
                    itemLabel={line.itemLabel}
                    extras={line.extras}
                    quantity={line.quantity}
                    maxQuantity={line.maxQuantity}
                    price={line.price}
                    onQuantityChange={line.onQuantityChange}
                    quantityLabel={line.quantityLabel}
                    disabled={line.disabled}
                />
            ))}
        </div>
    ));
}

type BasketLineRowProps = {
    itemLabel: string;
    extras?: React.ReactNode;
    quantity: number;
    maxQuantity: number;
    price: string;
    onQuantityChange: (quantity: number) => void;
    quantityLabel: string;
    disabled?: boolean;
};

function BasketLineRow({ itemLabel, extras, quantity, maxQuantity, price, onQuantityChange, quantityLabel, disabled }: BasketLineRowProps) {
    return (
        <div className="basket-line">
            <div className="basket-line-row">
                <div className="basket-line-name">{itemLabel}</div>
                <div className="basket-line-right">
                    <QuantitySelect
                        variant="basket"
                        className="basket-line-qty"
                        value={quantity}
                        onChange={onQuantityChange}
                        maxQuantity={maxQuantity}
                        disabled={disabled}
                        ariaLabel={quantityLabel}
                    />
                    <span className="basket-line-price">{price}</span>
                </div>
            </div>
            {extras}
        </div>
    );
}
